import React, { useState } from 'react';
import Panel from '../../components/ui/Panel';
import Button from '../../components/ui/Button';

const ButtonEventHandling: React.FC = () => {
  const [clickCount, setClickCount] = useState(0);
  const [hoverState, setHoverState] = useState('Not hovering');

  return (
    <div className="space-y-8">
      <h1 className="text-3xl text-osrs-gold font-bold uppercase mb-4">Button Event Handling</h1>
      
      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Click Events</h2>
        <Panel>
          <div className="space-y-4">
            <p className="text-osrs-yellow">
              Handle click events using the onClick prop. The event handler receives a React.MouseEvent.
            </p>
            <pre className="bg-osrs-black p-4 rounded font-mono text-sm text-osrs-yellow overflow-x-auto">
{`const handleClick = (e: React.MouseEvent) => {
  // Handle the click event
  console.log('Button clicked!');
};

<Button onClick={handleClick}>
  Click Me
</Button>`}
            </pre>
            <div className="mt-4">
              <p className="text-osrs-yellow mb-2">Live example:</p>
              <div className="flex items-center gap-4">
                <Button onClick={() => setClickCount(prev => prev + 1)}>
                  Click Counter
                </Button>
                <span className="text-osrs-yellow">Clicks: {clickCount}</span>
              </div>
            </div>
          </div>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Hover Events</h2>
        <Panel>
          <div className="space-y-4">
            <p className="text-osrs-yellow">
              Handle hover events using onMouseEnter and onMouseLeave props.
            </p>
            <pre className="bg-osrs-black p-4 rounded font-mono text-sm text-osrs-yellow overflow-x-auto">
{`<Button 
  onMouseEnter={() => console.log('Hover started')}
  onMouseLeave={() => console.log('Hover ended')}
>
  Hover Me
</Button>`}
            </pre>
            <div className="mt-4">
              <p className="text-osrs-yellow mb-2">Live example:</p>
              <div className="flex items-center gap-4">
                <Button 
                  onMouseEnter={() => setHoverState('Hovering')}
                  onMouseLeave={() => setHoverState('Not hovering')}
                >
                  Hover Me
                </Button>
                <span className="text-osrs-yellow">Status: {hoverState}</span>
              </div>
            </div>
          </div>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Disabled State</h2>
        <Panel>
          <div className="space-y-4">
            <p className="text-osrs-yellow">
              Disabled buttons will not trigger any events. Use the disabled prop to control this state.
            </p>
            <pre className="bg-osrs-black p-4 rounded font-mono text-sm text-osrs-yellow overflow-x-auto">
{`<Button 
  disabled={isDisabled}
  onClick={() => console.log('This won\'t trigger when disabled')}
>
  Disabled Button
</Button>`}
            </pre>
            <div className="mt-4">
              <p className="text-osrs-yellow mb-2">Live example:</p>
              <Button disabled>
                Disabled Button
              </Button>
            </div>
          </div>
        </Panel>
      </section>

      <section>
        <h2 className="text-2xl text-osrs-gold font-bold mb-4">Best Practices</h2>
        <Panel>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2">
            <li>Always provide feedback for user interactions</li>
            <li>Use appropriate cursor styles (pointer for enabled, not-allowed for disabled)</li>
            <li>Include keyboard navigation support</li>
            <li>Add loading states for async operations</li>
            <li>Maintain ARIA attributes for accessibility</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default ButtonEventHandling; 